import React, { useRef, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { useMakeSelector, useSelector } from 'hooks/redux'
import { useMakeDispatchCallback } from 'hooks'

import { makeAffinityGroupSelector } from 'selectors/feature/affinityGroups'
import {
  makeIsOutputQueryInOtherFunction,
  getShowOutputEvaluation,
} from 'selectors/feature/outputs'
import { toggleAffinityHighlight } from 'actions/feature/affinityGroups'
import { setComponentMeta } from 'actions/navigator'

import { CARRY_TO } from 'utils/constants'
import { EVAL_VALUES } from 'components/utils'
import NodeConnector from 'components/feature/NodeConnector'
import OutputTypeDescriptionLabel from 'components/feature/OutputNode/OutputTypeDescriptionLabel'
import QuickAddLogicMenu from 'components/feature/QuickAddLogicMenu'
import {
  StyledOutputType,
  StyledEvaluationBadge,
  StyledCarryTo,
} from './OutputNodeType.style'

const OutputNodeType = ({
  output,
  outputQueryUri,
  isSelected,
  showAddOption,
  isSimulating,
}) => {
  const outputNodeTypeRef = useRef(null)
  const dispatch = useDispatch()
  const setNavigatorComponentMeta = useMakeDispatchCallback(setComponentMeta)

  const { affinityGroupNumber, highlightAffinity } = useMakeSelector(
    makeAffinityGroupSelector,
    { id: outputQueryUri }
  )

  const {
    id,
    fnId,
    value: outputValue,
    mappedOutputId,
    type,
    outputDescription,
    description,
    connector,
    location,
    numberOfBits,
  } = output

  const showOutputEvaluation = useSelector(getShowOutputEvaluation, {
    fnId,
    id,
  })
  const isOutputQueryInOtherFunction = useMakeSelector(
    makeIsOutputQueryInOtherFunction,
    { fnId }
  )

  useEffect(() => {
    setNavigatorComponentMeta(id, {
      type: 'outputBlock',
      parentId: fnId,
      getRef: () => outputNodeTypeRef,
      fnId,
    })
  }, [setNavigatorComponentMeta, id, outputNodeTypeRef, fnId])

  return (
    <StyledOutputType
      ref={outputNodeTypeRef}
      className="output-node-type"
      isSelected={isSelected}
    >
      {showOutputEvaluation && (
        <StyledEvaluationBadge
          value={outputValue === EVAL_VALUES.TRUE}
          selected={isSelected}
        />
      )}
      {isOutputQueryInOtherFunction && (
        <StyledCarryTo
          carryToType={CARRY_TO}
          onClick={() => dispatch(toggleAffinityHighlight(outputQueryUri))}
          highlightAffinity={highlightAffinity}
          affinityGroupNumber={affinityGroupNumber}
        />
      )}

      {showAddOption && !isOutputQueryInOtherFunction && (
        <QuickAddLogicMenu output={output} />
      )}

      <NodeConnector
        rungNodeUri={id}
        carryNodeUri={outputQueryUri}
        fnId={fnId}
        showAddOption={showAddOption}
      />
      <OutputTypeDescriptionLabel
        id={id}
        fnId={fnId}
        mappedOutputId={mappedOutputId}
        type={type}
        outputDescription={outputDescription}
        description={description}
        connector={connector}
        location={location}
        numberOfBits={numberOfBits}
      />
    </StyledOutputType>
  )
}

OutputNodeType.propTypes = {
  output: PropTypes.object,
  outputQueryUri: PropTypes.string,
  isSelected: PropTypes.bool,
  showAddOption: PropTypes.bool,
  isSimulating: PropTypes.bool,
}

export default OutputNodeType
